import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — full-screen photo viewer with prev/next/close.
 * Props: images: [url], startIndex, onClose()
 */
const Lightbox = ({ images, startIndex = 0, onClose }) => {
  const [i, setI] = useState(startIndex);
  const total = images?.length || 0;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight" && total > 1) setI((n) => (n + 1) % total);
      else if (e.key === "ArrowLeft" && total > 1) setI((n) => (n - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [total, onClose]);

  if (!total) return null;
  const src = images[i];
  const next = () => setI((n) => (n + 1) % total);
  const prev = () => setI((n) => (n - 1 + total) % total);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="lightbox"
    >
      <div className="flex items-center justify-between px-4 py-3 text-[#F5EFE6]" onClick={(e) => e.stopPropagation()}>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5EFE6]/70">
          Photo {i + 1} / {total}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10"
          aria-label="Close"
          data-testid="lightbox-close"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-4 pb-4" onClick={(e) => e.stopPropagation()}>
        {total > 1 && (
          <button
            type="button"
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-[#F5EFE6]"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        <img
          src={src}
          alt=""
          className="max-h-full max-w-full object-contain rounded-md"
        />
        {total > 1 && (
          <button
            type="button"
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-[#F5EFE6]"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight size={22} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
